import { useCallback, useEffect, useId, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { VisorDeCfdi } from '../componentes/VisorDeCfdi'
import { useTrampaDeFoco } from '../ganchos/useTrampaDeFoco'
import {
  api,
  CfdiDelPanel,
  Cliente,
  CuentaDeCatalogo,
  EstadoContabilizacionCfdi,
  ErrorDeApi,
  Poliza,
  PolizaSugerida,
  ReglaDeContabilizacion,
  ResumenDePanel,
} from '../servicios/api'
import { CapturaDePoliza } from './Polizas'
import { telon } from '../ganchos/telon'
import { useAvisos } from '../componentes/Toast'

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']

const dinero = (n: number) =>
  n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const fechaCorta = (iso: string) =>
  new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })

/** Los 24 periodos que un despacho revisa en la práctica, en un solo control. */
function periodosDisponibles(hoy: Date) {
  const lista: { anio: number; mes: number }[] = []
  for (let i = 0; i < 24; i++) {
    const d = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1)
    lista.push({ anio: d.getFullYear(), mes: d.getMonth() + 1 })
  }
  return lista
}

/** El chip de la cola: el estado del comprobante resumido en una palabra. */
function chipDe(c: CfdiDelPanel): { texto: string; grado: string } {
  if (c.estado === 'Contabilizado') return { texto: 'Contabilizada', grado: 'cerrada' }
  if (c.estado === 'Descartado') return { texto: 'Descartada', grado: 'descartada' }
  return c.tieneReglaActiva
    ? { texto: 'Regla lista', grado: 'suave' }
    : { texto: 'Sin regla', grado: 'aviso' }
}

const nombreDeLaContraparte = (c: CfdiDelPanel) =>
  (c.esEmitido ? c.nombreReceptor || c.rfcReceptor : c.nombreEmisor || c.rfcEmisor)

interface Comprobacion {
  clave: string
  estado: 'ok' | 'pendiente'
  texto: string
}

/**
 * Panel de trabajo por cliente y periodo: cada CFDI aparece como pendiente, contabilizado
 * o descartado, sin que ninguno pueda perderse. SRS: RF-601, RF-604 · RN-09. Cédula: E-01, E-03.
 *
 * Tres columnas simultáneas —cola, comprobante y póliza propuesta— según el prototipo
 * aprobado. Antes era una tabla con cuatro botones por renglón y modales encadenados: para
 * ver el CFDI había que abrir uno, cerrarlo, y pedir la sugerencia a ciegas. Aquí el
 * comprobante y su asiento propuesto están a la vista al mismo tiempo, que es la única
 * forma de revisar lo segundo contra lo primero.
 */
export function PanelDeContabilizacion() {
  const { id: clienteId } = useParams<{ id: string }>()
  const [hoy] = useState(() => new Date())
  const [anio, setAnio] = useState(hoy.getFullYear())
  const [mes, setMes] = useState(hoy.getMonth() + 1)
  const [cliente, setCliente] = useState<Cliente>()
  const [resumen, setResumen] = useState<ResumenDePanel>()
  const [items, setItems] = useState<CfdiDelPanel[]>()
  const [filtro, setFiltro] = useState<EstadoContabilizacionCfdi | undefined>('Pendiente')
  const [seleccionadoId, setSeleccionadoId] = useState<string>()
  const [error, setError] = useState('')
  const avisar = useAvisos()

  const [sugerencia, setSugerencia] = useState<PolizaSugerida | null>(null)
  const [sugiriendo, setSugiriendo] = useState(false)
  const [errorSugerencia, setErrorSugerencia] = useState('')
  const [registrando, setRegistrando] = useState(false)

  const [capturaDe, setCapturaDe] = useState<{ cfdi?: CfdiDelPanel; sugerida?: PolizaSugerida } | null>(null)
  const [descartando, setDescartando] = useState<CfdiDelPanel | null>(null)
  const [vinculandoA, setVinculandoA] = useState<CfdiDelPanel | null>(null)
  const idTituloVincular = useId()
  const panelVincularRef = useTrampaDeFoco<HTMLDivElement>(vinculandoA !== null, () => setVinculandoA(null))
  const [polizasAbiertas, setPolizasAbiertas] = useState<Poliza[]>([])
  const [seleccionados, setSeleccionados] = useState<Set<string>>(new Set())
  const [reglasAbiertas, setReglasAbiertas] = useState(false)
  const [rfcPrecargado, setRfcPrecargado] = useState('')

  const recargar = useCallback(async () => {
    if (!clienteId) return
    try {
      const [datosCliente, r, lista] = await Promise.all([
        api.obtenerCliente(clienteId),
        api.panelResumen(clienteId, anio, mes),
        api.panelListarCfdi(clienteId, anio, mes, filtro),
      ])
      setCliente(datosCliente)
      setResumen(r)
      setItems(lista)
      setError('')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar el panel de contabilización.')
    }
  }, [clienteId, anio, mes, filtro])

  useEffect(() => {
    setSeleccionados(new Set())
    // La selección es de un periodo concreto: al cambiar de mes no puede quedar apuntando
    // a un comprobante que ya no está en la cola.
    setSeleccionadoId(undefined)
    void recargar()
  }, [recargar])

  const seleccionado = useMemo(
    () => items?.find((c) => c.id === seleccionadoId) ?? items?.[0],
    [items, seleccionadoId],
  )

  // La sugerencia se pide al seleccionar, no al pulsar un botón: la columna derecha existe
  // para mostrarla, y `tieneReglaActiva` ya dice de antemano si tiene sentido pedirla.
  useEffect(() => {
    setSugerencia(null)
    setErrorSugerencia('')
    if (!clienteId || !seleccionado) return
    if (seleccionado.estado !== 'Pendiente' || !seleccionado.tieneReglaActiva) return

    let vigente = true
    setSugiriendo(true)
    api.sugerirPoliza(clienteId, seleccionado.id)
      .then((s) => { if (vigente) setSugerencia(s) })
      .catch((e) => {
        if (vigente) setErrorSugerencia(e instanceof ErrorDeApi ? e.message : 'No se pudo generar la sugerencia.')
      })
      .finally(() => { if (vigente) setSugiriendo(false) })
    return () => { vigente = false }
  }, [clienteId, seleccionado?.id, seleccionado?.estado, seleccionado?.tieneReglaActiva])

  if (error && !items) return <p className="error" role="alert">{error}</p>
  if (!clienteId || !cliente || !resumen || !items) return <p>Cargando…</p>

  async function descartar(motivo: string) {
    if (!descartando) return
    const item = descartando
    try {
      await api.descartarCfdiDelPanel(clienteId!, item.id, motivo)
      setDescartando(null)
      avisar(`CFDI ${item.uuid.slice(0, 8)}… descartado.`)
      await recargar()
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo descartar el CFDI.')
    }
  }

  async function restaurar(item: CfdiDelPanel) {
    try {
      await api.restaurarCfdiDelPanel(clienteId!, item.id)
      avisar(`CFDI ${item.uuid.slice(0, 8)}… restaurado a pendiente.`)
      await recargar()
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo restaurar el CFDI.')
    }
  }

  async function abrirVinculacion(item: CfdiDelPanel) {
    try {
      const lista = await api.listarPolizas(clienteId!, anio, mes)
      setPolizasAbiertas(lista.filter((p) => p.estado === 'Vigente'))
      setVinculandoA(item)
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudieron cargar las pólizas del periodo.')
    }
  }

  async function vincular(polizaId: string) {
    if (!vinculandoA) return
    try {
      await api.vincularCfdi(clienteId!, polizaId, vinculandoA.uuid, vinculandoA.rfcEmisor, vinculandoA.total)
      avisar(`CFDI ${vinculandoA.uuid.slice(0, 8)}… vinculado a la póliza.`)
      setVinculandoA(null)
      await recargar()
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo vincular el CFDI a la póliza.')
    }
  }

  /**
   * Registra la póliza propuesta tal cual, sin pasar por la captura. Es el atajo que
   * justifica la columna: el contador ya vio el CFDI, la regla y los tres renglones.
   */
  async function confirmarYRegistrar() {
    if (!sugerencia || registrando) return
    setError('')
    setRegistrando(true)
    try {
      const poliza = await api.registrarPoliza(clienteId!, {
        tipo: 'Diario',
        fecha: sugerencia.fecha,
        concepto: sugerencia.concepto,
        movimientos: sugerencia.movimientos.map((m) => ({
          cuentaId: m.cuentaId,
          cargo: m.cargo,
          abono: m.abono,
          moneda: 'MXN',
          etiquetaFiscal: m.etiquetaFiscal,
        })),
        uuids: sugerencia.uuids,
      })
      avisar(`Póliza ${poliza.folioVisible} registrada; el UUID quedó conciliado.`)
      setSeleccionados(new Set())
      await recargar()
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo registrar la póliza sugerida.')
    } finally {
      setRegistrando(false)
    }
  }

  async function sugerirLote() {
    setError('')
    setSugiriendo(true)
    try {
      const s = await api.sugerirPolizaLote(clienteId!, [...seleccionados])
      setCapturaDe({ sugerida: s })
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo generar la sugerencia en lote.')
    } finally {
      setSugiriendo(false)
    }
  }

  function alternarSeleccion(id: string) {
    setSeleccionados((actual) => {
      const copia = new Set(actual)
      if (copia.has(id)) copia.delete(id)
      else copia.add(id)
      return copia
    })
  }

  function comprobaciones(s: PolizaSugerida): Comprobacion[] {
    const cargos = s.movimientos.reduce((suma, m) => suma + m.cargo, 0)
    const abonos = s.movimientos.reduce((suma, m) => suma + m.abono, 0)
    const cuadra = cargos > 0 && Math.abs(cargos - abonos) < 0.005
    const coincide = Math.abs(cargos - s.totalCfdi) < 0.005
    return [
      {
        clave: 'partida-doble',
        estado: cuadra ? 'ok' : 'pendiente',
        texto: cuadra
          ? `Partida doble cuadrada: cargos = abonos = ${dinero(cargos)}`
          : `Partida doble descuadrada: cargos ${dinero(cargos)} contra abonos ${dinero(abonos)}`,
      },
      {
        clave: 'importes',
        estado: coincide ? 'ok' : 'pendiente',
        texto: coincide
          ? `Los importes cuadran con el CFDI (${dinero(s.totalCfdi)})`
          : `Los importes no cuadran con el CFDI: ${dinero(cargos)} contra ${dinero(s.totalCfdi)}`,
      },
      {
        clave: 'uuid',
        estado: s.uuids.length > 0 ? 'ok' : 'pendiente',
        texto: s.uuids.length > 0
          ? `El UUID quedará conciliado con la póliza (${s.uuids.length})`
          : 'La póliza quedaría sin UUID conciliado',
      },
    ]
  }

  const periodos = periodosDisponibles(hoy)
  // Del resumen y no de `items`: la lista viene filtrada, y con el filtro en «Contabilizados»
  // el encabezado diría «0 pendientes en cola» teniendo el periodo lleno de pendientes.
  const restoDelPeriodo = Math.max(resumen.total - resumen.pendientes, 0)

  return (
    <div className="pantalla-contabilizacion">
      <p className="sutil" style={{ padding: '0 var(--space-6)', marginTop: 'var(--space-4)' }}>
        <Link to={`/clientes/${clienteId}`}>← {cliente.razonSocial}</Link>
      </p>

      <header className="encabezado linea-base">
        <h1>Contabilización</h1>
        <select
          className="compacta"
          style={{ width: 'auto' }}
          aria-label="Periodo"
          value={`${anio}-${mes}`}
          onChange={(e) => {
            const [a, m] = e.target.value.split('-')
            setAnio(Number(a))
            setMes(Number(m))
          }}
        >
          {periodos.map((p) => (
            <option key={`${p.anio}-${p.mes}`} value={`${p.anio}-${p.mes}`}>
              {MESES[p.mes - 1]} {p.anio}
            </option>
          ))}
        </select>
        <span className="encabezado-sub">
          <strong className="num">{resumen.pendientes}</strong> pendientes en cola · {restoDelPeriodo} más en el periodo ·
          conciliación UUID ↔ póliza visible
        </span>
        <span className="encabezado-acciones">
          <button data-recorrido="contabilizacion-lote" disabled={seleccionados.size === 0 || sugiriendo} onClick={() => void sugerirLote()}>
            {sugiriendo && seleccionados.size > 0
              ? 'Generando…'
              : seleccionados.size > 0
                ? `Contabilizar en lote (${seleccionados.size})`
                : 'Contabilizar en lote'}
          </button>
          <button onClick={() => { setRfcPrecargado(''); setReglasAbiertas(true) }}>Reglas…</button>
        </span>
      </header>

      <div className="filtros" data-recorrido="contabilizacion-filtros" style={{ padding: '0 var(--space-6) var(--space-3)' }}>
        {([undefined, 'Pendiente', 'Contabilizado', 'Descartado'] as (EstadoContabilizacionCfdi | undefined)[]).map((e) => (
          <button key={e ?? 'todos'} className={e === filtro ? 'primario' : ''} onClick={() => setFiltro(e)}>
            {e === undefined && `Todos · ${resumen.total}`}
            {e === 'Pendiente' && `Pendientes · ${resumen.pendientes}`}
            {e === 'Contabilizado' && `Contabilizados · ${resumen.contabilizados}`}
            {e === 'Descartado' && `Descartados · ${resumen.descartados}`}
          </button>
        ))}
        <span className="sutil" style={{ margin: 0, alignSelf: 'center' }}>
          Ningún CFDI del periodo se pierde: {resumen.pendientes} pendiente(s) +{' '}
          {resumen.contabilizados} contabilizado(s) + {resumen.descartados} descartado(s) ={' '}
          {resumen.total} en total.
        </span>
      </div>

      {error && <p className="error" role="alert" style={{ margin: '0 var(--space-6) var(--space-3)' }}>{error}</p>}

      <div className="rejilla-contabilizacion">
        <aside className="cola" aria-label="Cola de CFDI">
          <div className="cola-cabecera kicker">Cola de CFDI</div>
          {items.length === 0 ? (
            <p className="vacio">Sin CFDI con este filtro en el periodo.</p>
          ) : (
            items.map((c) => {
              const chip = chipDe(c)
              return (
                <div key={c.id} style={{ display: 'flex', alignItems: 'stretch' }}>
                  {/* La casilla guarda su lugar aunque el CFDI ya no se pueda marcar: si no,
                      los renglones de la cola se escalonan según el estado de cada uno. */}
                  <label className="casilla" style={{ width: 26, paddingLeft: 10, justifyContent: 'flex-start' }}>
                    {c.estado === 'Pendiente' && (
                      <input
                        type="checkbox"
                        checked={seleccionados.has(c.id)}
                        onChange={() => alternarSeleccion(c.id)}
                        aria-label={`Incluir ${nombreDeLaContraparte(c)} en el lote`}
                      />
                    )}
                  </label>
                  <button
                    type="button"
                    className="cola-item"
                    style={{ flex: 1 }}
                    aria-current={c.id === seleccionado?.id ? 'true' : undefined}
                    onClick={() => setSeleccionadoId(c.id)}
                  >
                    <span className="cola-linea">
                      <strong>{nombreDeLaContraparte(c)}</strong>
                      <span className="num">{dinero(c.total)}{c.moneda !== 'MXN' ? ` ${c.moneda}` : ''}</span>
                    </span>
                    <span className="cola-linea cola-meta">
                      <span>{fechaCorta(c.fechaEmision)}</span>
                      <span>· {c.reglaDescripcion ?? chip.texto}</span>
                      <span className={`insignia diminuta ${chip.grado}`}>{chip.texto}</span>
                    </span>
                  </button>
                </div>
              )
            })
          )}
          <div className="cola-pie">Los descartados permanecen en el repositorio.</div>
        </aside>

        <section className="detalle-cfdi" aria-label="Detalle del comprobante">
          {seleccionado
            ? <VisorDeCfdi clienteId={clienteId} cfdi={seleccionado} />
            : <p className="vacio">Selecciona un CFDI de la cola para verlo aquí.</p>}
        </section>

        <section className="panel-poliza" aria-label="Póliza sugerida">
          <div className="panel-cabecera en-columna">
            <span className="kicker">Póliza sugerida</span>
          </div>

          {!seleccionado && (
            <div className="panel-poliza-cuerpo">
              <p className="vacio">Sin comprobante seleccionado.</p>
            </div>
          )}

          {seleccionado?.estado === 'Contabilizado' && (
            <div className="panel-poliza-cuerpo">
              <div className="aviso-cerrado">
                <strong>Contabilizada ✓</strong> Póliza {seleccionado.polizaFolioVisible ?? '—'} registrada;
                el UUID quedó conciliado y visible en el estado UUID ↔ póliza.
              </div>
              {seleccionado.polizaId && (
                <p style={{ margin: 0 }}>
                  <Link to={`/clientes/${clienteId}/polizas`}>
                    Ver póliza {seleccionado.polizaFolioVisible ?? ''}
                  </Link>
                </p>
              )}
            </div>
          )}

          {seleccionado?.estado === 'Descartado' && (
            <div className="panel-poliza-cuerpo">
              <div className="aviso-inerte">
                Descartado{seleccionado.motivoDescarte ? `: ${seleccionado.motivoDescarte}` : ''}. El CFDI
                permanece en el repositorio y puede reactivarse desde aquí.
              </div>
              <div className="acciones inicio">
                <button onClick={() => void restaurar(seleccionado)}>Restaurar</button>
              </div>
            </div>
          )}

          {seleccionado?.estado === 'Pendiente' && !seleccionado.tieneReglaActiva && (
            <div className="panel-poliza-cuerpo">
              <div className="aviso-critico">
                <p style={{ margin: 0 }}>
                  <strong>Sin regla aplicable para este emisor.</strong> Crea una regla del cliente
                  (proveedor → cuenta de gasto, IVA, retenciones) o captura la póliza manualmente.
                </p>
              </div>
              <div className="acciones inicio">
                <button
                  className="primario"
                  onClick={() => { setRfcPrecargado(seleccionado.rfcEmisor); setReglasAbiertas(true) }}
                >
                  Crear regla
                </button>
                <button onClick={() => setCapturaDe({ cfdi: seleccionado })}>Capturar manual</button>
                <button onClick={() => void abrirVinculacion(seleccionado)}>Vincular…</button>
                <button className="fantasma" onClick={() => setDescartando(seleccionado)}>Descartar…</button>
              </div>
            </div>
          )}

          {seleccionado?.estado === 'Pendiente' && seleccionado.tieneReglaActiva && (
            <div className="panel-poliza-cuerpo">
              {seleccionado.reglaDescripcion && (
                <div className="regla-aplicada">Regla aplicada: <strong>{seleccionado.reglaDescripcion}</strong></div>
              )}

              {errorSugerencia && <p className="error" role="alert">{errorSugerencia}</p>}
              {sugiriendo && !sugerencia && <p className="sutil" style={{ margin: 0 }}>Generando la sugerencia…</p>}

              {sugerencia && (
                <>
                  <table className="tabla densa sobre-blanco">
                    <thead>
                      <tr><th>Cuenta</th><th className="num">Debe</th><th className="num">Haber</th></tr>
                    </thead>
                    <tbody>
                      {sugerencia.movimientos.map((m, i) => (
                        <tr key={`${m.cuentaId}-${i}`}>
                          <td className="mono">{m.cuentaNumero} · {m.cuentaNombre}</td>
                          <td className="num">{m.cargo ? dinero(m.cargo) : ''}</td>
                          <td className="num">{m.abono ? dinero(m.abono) : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="comprobaciones">
                    {comprobaciones(sugerencia).map((c) => (
                      <p key={c.clave} className={c.estado === 'ok' ? 'comprobacion menuda' : 'comprobacion menuda pendiente'}>
                        <span aria-hidden="true">{c.estado === 'ok' ? '✓' : '⚠'}</span>
                        <span>{c.texto}</span>
                      </p>
                    ))}
                  </div>
                </>
              )}

              <div className="acciones inicio">
                <button
                  className="primario"
                  disabled={!sugerencia || registrando}
                  onClick={() => void confirmarYRegistrar()}
                >
                  {registrando ? 'Registrando…' : 'Confirmar y registrar'}
                </button>
                <button
                  onClick={() => setCapturaDe({ cfdi: seleccionado, sugerida: sugerencia ?? undefined })}
                >
                  Editar en captura
                </button>
                <button onClick={() => void abrirVinculacion(seleccionado)}>Vincular…</button>
                <button className="fantasma" onClick={() => setDescartando(seleccionado)}>Descartar…</button>
              </div>

              <p className="panel-poliza-pie">Nada se registra sin revisión y confirmación del contador.</p>
            </div>
          )}
        </section>
      </div>

      {capturaDe && (
        <CapturaDePoliza
          clienteId={clienteId}
          anio={anio}
          mes={mes}
          uuidsPrefill={capturaDe.sugerida?.uuids ?? (capturaDe.cfdi ? [capturaDe.cfdi.uuid] : undefined)}
          conceptoPrefill={
            capturaDe.sugerida?.concepto
            ?? (capturaDe.cfdi ? nombreDeLaContraparte(capturaDe.cfdi) : undefined)
          }
          fechaPrefill={capturaDe.sugerida?.fecha}
          movimientosPrefill={capturaDe.sugerida?.movimientos.map((m) => ({
            cuentaId: m.cuentaId, cargo: m.cargo, abono: m.abono, etiquetaFiscal: m.etiquetaFiscal,
          }))}
          alCerrar={(mensaje) => {
            setCapturaDe(null)
            setSeleccionados(new Set())
            if (mensaje) avisar(mensaje)
            void recargar()
          }}
        />
      )}

      {descartando && (
        <MotivoDeDescarte
          cfdi={descartando}
          alCerrar={() => setDescartando(null)}
          alConfirmar={(motivo) => void descartar(motivo)}
        />
      )}

      {reglasAbiertas && (
        <ReglasModal
          clienteId={clienteId}
          rfcPrecargado={rfcPrecargado}
          alCerrar={() => { setReglasAbiertas(false); void recargar() }}
        />
      )}

      {vinculandoA && (
        <div className="modal" {...telon(() => setVinculandoA(null))}>
          <div
            ref={panelVincularRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={idTituloVincular}
            className="panel"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id={idTituloVincular}>Vincular CFDI a una póliza existente</h2>
            <p className="sutil">CFDI {vinculandoA.uuid} · {dinero(vinculandoA.total)}</p>
            {polizasAbiertas.length === 0 ? (
              <p className="vacio">No hay pólizas vigentes en este periodo. Usa «Capturar manual» en su lugar.</p>
            ) : (
              <table className="tabla">
                <thead><tr><th>Folio</th><th>Concepto</th><th></th></tr></thead>
                <tbody>
                  {polizasAbiertas.map((p) => (
                    <tr key={p.id}>
                      <td className="mono">{p.folioVisible}</td>
                      <td>{p.concepto}</td>
                      <td><button className="enlace" onClick={() => void vincular(p.id)}>Vincular</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="acciones">
              <button onClick={() => setVinculandoA(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Motivo del descarte en un modal propio. Antes era `window.prompt`, que no se puede
 * estilar, no admite trampa de foco y en algunos navegadores el usuario puede silenciarlo
 * para siempre — con lo que descartar dejaba de funcionar sin explicación. El motivo es
 * obligatorio porque queda en el rastro de auditoría (RN-09).
 */
function MotivoDeDescarte({
  cfdi,
  alCerrar,
  alConfirmar,
}: {
  cfdi: CfdiDelPanel
  alCerrar: () => void
  alConfirmar: (motivo: string) => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLDivElement>(true, alCerrar)
  const [motivo, setMotivo] = useState('')

  return (
    <div className="modal" {...telon(() => alCerrar())}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={idTitulo}>Descartar CFDI</h2>
        <p className="sutil">
          {cfdi.uuid} · {dinero(cfdi.total)}. El comprobante permanece en el repositorio y puede
          restaurarse; el motivo queda en el rastro.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (motivo.trim()) alConfirmar(motivo.trim())
          }}
        >
          <label>
            Motivo del descarte
            <input
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              required
              maxLength={300}
              placeholder="Duplicado del folio F-118"
            />
          </label>
          <div className="acciones">
            <button type="button" onClick={alCerrar}>Cancelar</button>
            <button type="submit" className="primario" disabled={!motivo.trim()}>Descartar</button>
          </div>
        </form>
      </div>
    </div>
  )
}

/**
 * Reglas de contabilización por proveedor: qué cuentas usar al proponer una póliza desde un
 * CFDI. Se desactivan, nunca se eliminan (RN-09). SRS: RF-602. Cédula: E-02.
 */
function ReglasModal({
  clienteId,
  rfcPrecargado,
  alCerrar,
}: {
  clienteId: string
  /** RFC del emisor que disparó «Crear regla» desde la columna de la póliza sugerida. */
  rfcPrecargado?: string
  alCerrar: () => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLDivElement>(true, alCerrar)
  const [reglas, setReglas] = useState<ReglaDeContabilizacion[]>([])
  const [cuentas, setCuentas] = useState<CuentaDeCatalogo[]>([])
  const [rfcEmisor, setRfcEmisor] = useState(rfcPrecargado ?? '')
  const [cuentaGastoId, setCuentaGastoId] = useState('')
  const [cuentaContrapartidaId, setCuentaContrapartidaId] = useState('')
  const [cuentaIvaAcreditableId, setCuentaIvaAcreditableId] = useState('')
  const [cuentaIsrRetenidoId, setCuentaIsrRetenidoId] = useState('')
  const [cuentaIvaRetenidoId, setCuentaIvaRetenidoId] = useState('')
  const [descripcion, setDescripcion] = useState('')
  const [error, setError] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [editando, setEditando] = useState<ReglaDeContabilizacion | null>(null)

  async function recargar() {
    try {
      const [listaReglas, todasCuentas] = await Promise.all([
        api.listarReglas(clienteId),
        api.catalogoDeCuentas(clienteId),
      ])
      setReglas(listaReglas)
      setCuentas(todasCuentas.filter((c) => c.esDeRegistro && c.estado === 'Activa'))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar las reglas.')
    }
  }

  useEffect(() => {
    void recargar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId])

  function limpiarFormulario() {
    setEditando(null)
    setRfcEmisor(rfcPrecargado ?? ''); setCuentaGastoId(''); setCuentaContrapartidaId('')
    setCuentaIvaAcreditableId(''); setCuentaIsrRetenidoId(''); setCuentaIvaRetenidoId(''); setDescripcion('')
  }

  /** Precarga el formulario con la regla elegida (RF-602, cédula E-02: "la regla es visible y editable"). */
  function comenzarEdicion(regla: ReglaDeContabilizacion) {
    setError('')
    setEditando(regla)
    setRfcEmisor(regla.rfcEmisor)
    setCuentaGastoId(regla.cuentaGastoId)
    setCuentaContrapartidaId(regla.cuentaContrapartidaId)
    setCuentaIvaAcreditableId(regla.cuentaIvaAcreditableId ?? '')
    setCuentaIsrRetenidoId(regla.cuentaIsrRetenidoId ?? '')
    setCuentaIvaRetenidoId(regla.cuentaIvaRetenidoId ?? '')
    setDescripcion(regla.descripcion ?? '')
  }

  async function guardar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    setError('')
    setGuardando(true)
    const datos = {
      rfcEmisor,
      cuentaGastoId,
      cuentaContrapartidaId,
      cuentaIvaAcreditableId: cuentaIvaAcreditableId || undefined,
      cuentaIsrRetenidoId: cuentaIsrRetenidoId || undefined,
      cuentaIvaRetenidoId: cuentaIvaRetenidoId || undefined,
      descripcion: descripcion || undefined,
    }
    try {
      if (editando) await api.actualizarRegla(clienteId, editando.id, datos)
      else await api.registrarRegla(clienteId, datos)
      limpiarFormulario()
      await recargar()
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : `No se pudo ${editando ? 'guardar' : 'crear'} la regla.`)
    } finally {
      setGuardando(false)
    }
  }

  async function alternar(regla: ReglaDeContabilizacion) {
    try {
      if (regla.estado === 'Activa') await api.desactivarRegla(clienteId, regla.id)
      else await api.reactivarRegla(clienteId, regla.id)
      await recargar()
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo cambiar el estado de la regla.')
    }
  }

  return (
    <div className="modal" {...telon(() => alCerrar())}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(720px, 94vw)' }}
      >
        <h2 id={idTitulo}>Reglas de contabilización</h2>
        <p className="sutil">
          Por proveedor (RFC emisor): qué cuenta de gasto, IVA y retenciones usar al sugerir una póliza desde
          un CFDI (RF-602). El contador siempre revisa y confirma antes de registrarla.
        </p>

        {reglas.length === 0 ? (
          <p className="vacio">Sin reglas configuradas todavía.</p>
        ) : (
          <table className="tabla">
            <thead><tr><th>RFC</th><th>Gasto</th><th>IVA / retenciones</th><th>Estado</th><th></th></tr></thead>
            <tbody>
              {reglas.map((r) => (
                <tr key={r.id} style={{ opacity: r.estado === 'Desactivada' ? 0.5 : 1 }}>
                  <td className="mono">{r.rfcEmisor}{r.descripcion ? <div className="sutil" style={{ margin: 0 }}>{r.descripcion}</div> : null}</td>
                  <td>{r.cuentaGastoTexto}</td>
                  <td className="sutil">
                    {[r.cuentaIvaAcreditableTexto, r.cuentaIsrRetenidoTexto, r.cuentaIvaRetenidoTexto].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td><span className={`insignia ${r.estado === 'Activa' ? 'vigente' : 'vencido'}`}>{r.estado}</span></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="enlace" onClick={() => comenzarEdicion(r)}>Editar</button>{' '}
                    <button className="enlace" onClick={() => void alternar(r)}>{r.estado === 'Activa' ? 'Desactivar' : 'Reactivar'}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <form onSubmit={guardar} style={{ marginTop: 12 }}>
          <h3 style={{ fontSize: 14 }}>{editando ? `Editar regla de ${editando.rfcEmisor}` : 'Nueva regla'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
            <label>
              RFC del proveedor
              <input value={rfcEmisor} onChange={(e) => setRfcEmisor(e.target.value.toUpperCase())} required
                className="mono" maxLength={13} placeholder="ABC010101AB1" disabled={Boolean(editando)}
                title={editando ? 'El RFC del proveedor no se puede cambiar; da de baja la regla y crea una nueva.' : undefined} />
            </label>
            <label>
              Descripción (opcional)
              <input value={descripcion} onChange={(e) => setDescripcion(e.target.value)} maxLength={300}
                placeholder="Honorarios del contador externo" />
            </label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
            <label>
              Cuenta de gasto
              <select value={cuentaGastoId} onChange={(e) => setCuentaGastoId(e.target.value)} required>
                <option value="">— cuenta de registro —</option>
                {cuentas.map((c) => <option key={c.id} value={c.id}>{c.numero} · {c.nombre}</option>)}
              </select>
            </label>
            <label>
              Cuenta de contrapartida (banco/proveedores)
              <select value={cuentaContrapartidaId} onChange={(e) => setCuentaContrapartidaId(e.target.value)} required>
                <option value="">— cuenta de registro —</option>
                {cuentas.map((c) => <option key={c.id} value={c.id}>{c.numero} · {c.nombre}</option>)}
              </select>
            </label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-3)' }}>
            <label>
              IVA acreditable (opcional)
              <select value={cuentaIvaAcreditableId} onChange={(e) => setCuentaIvaAcreditableId(e.target.value)}>
                <option value="">—</option>
                {cuentas.map((c) => <option key={c.id} value={c.id}>{c.numero} · {c.nombre}</option>)}
              </select>
            </label>
            <label>
              ISR retenido (opcional)
              <select value={cuentaIsrRetenidoId} onChange={(e) => setCuentaIsrRetenidoId(e.target.value)}>
                <option value="">—</option>
                {cuentas.map((c) => <option key={c.id} value={c.id}>{c.numero} · {c.nombre}</option>)}
              </select>
            </label>
            <label>
              IVA retenido (opcional)
              <select value={cuentaIvaRetenidoId} onChange={(e) => setCuentaIvaRetenidoId(e.target.value)}>
                <option value="">—</option>
                {cuentas.map((c) => <option key={c.id} value={c.id}>{c.numero} · {c.nombre}</option>)}
              </select>
            </label>
          </div>

          {error && <p className="error" role="alert">{error}</p>}

          <div className="acciones">
            <button type="button" onClick={alCerrar}>Cerrar</button>
            {editando && <button type="button" onClick={limpiarFormulario}>Cancelar edición</button>}
            <button type="submit" className="primario" disabled={guardando}>
              {guardando ? 'Guardando…' : editando ? 'Guardar cambios' : 'Agregar regla'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
